
/****
 * API Route Constants
 * @author Karen Yunuen Pozos Arias
 *
 * This file centralizes the definition of service names and versioned base paths
 * for backend REST endpoints. It helps prevent string duplication, enables easy
 * refactoring, and keeps route construction consistent across the application.
 * The constants defined here are typically used by API clients such as Axios,
 * RTK Query, or Redux Thunks when performing HTTP requests.
 * ***/

export const FILE_UPLOAD_SERVICE = {
    name:'file-upload',
    baseV1:`file-upload/v1/`,
    zipDownload: `file-upload/v1/zip-download`,
    zipDownloadByUserId: `file-upload/v1/zip-download/userId/`,
    download: `file-upload/v1/download`
};

export const EVENT_SERVICE = {
    fileCreate: `event-service/setting/${FILE_UPLOAD_SERVICE.name}/create`,
    fileDeleteByIds: `event-service/setting/${FILE_UPLOAD_SERVICE.name}/delete-by-ids`
};

export const EQUIPMENT_SERVICE = {
    equipmentCheckInOut: 'equipment-service/v1/access-log/create',
    getAccessLogEntryById: 'equipment-service/v1/access-log/',
    checkInLog: '/check-in/log', // equipment-service/v1/access-log/{id}/check-in/log
    checkOutLog: '/check-out/log', // equipment-service/v1/access-log/{id}/check-out/log
    createEquipment: `equipment-service/v1/create`,
    createDeliveryAsset: `equipment-service/v1/delivery/create-asset`,
    getTrackByDeliveryId: `equipment-service/v1/tracker/get-all-by-delivery-id/`,
    uploadActivityTracker: `equipment-service/v1/tracker/create-asset`
}

export const LOG_SERVICE = {
    getAllVehicleAccessLogByVehicleId: `log-service/vehicle-access/get-all-pageable/vehicle-id/`
};

export const DOCUMENTATION_APPROVAL_SERVICE ={
    baseDocumentationUser : `documentation-approval-service/documentation-user/`,
    baseDocumentationVehicle : `documentation-approval-service/documentation-vehicle/`,
    createDocumentationApprovalFlow : `documentation-approval-service/create`,
    deleteDocumentationApprovalFlowByIds : `documentation-approval-service/delete-by-ids`,
    updateDocumentationApprovalFlow : `documentation-approval-service/update`,
    getDocumentationRequestByUserTypeIdAndProviderId : `documentation-approval-service/documentation-user/get-documentation-request/`,
    getDocumentationUserByUserId : `documentation-approval-service/documentation-user/user/`,
    documentationUserUpdateStatusById : `documentation-approval-service/documentation-user/status/by-id`,
    documentationUserUpdateStatusByUserIds : `documentation-approval-service/documentation-user/status/by-users-ids`,
    documentationVehicleUpdateStatusById : `documentation-approval-service/documentation-vehicle/status/by-id`,
    documentationVehicleUpdateStatusByVehicleIds : `documentation-approval-service/documentation-vehicle/status/by-vehicles-ids`,
    getActiveDocumentApprovalPeriod : `documentation-approval-service/documentation-period/active/user-type/`,
    getUploadDocumentApprovalPeriod : `documentation-approval-service/documentation-period/upload/user-type/`,
    getDocumentationApprovalFlowByOptions : `documentation-approval-service/get-by-options`,
    getDocumentApprovalFlowById : `documentation-approval-service/get-by-id/`,
    getActiveDocumentUserList : `/get-active-levels`, // documentation-approval-service/documentation-user/{userTypeId}/{userId}/{profile}/get-active-levels
    getActiveDocumentVehicleList : `/get-active-levels`, // documentation-approval-service/documentation-vehicle/{userTypeId}/{vehicleId}/get-active-levels
    getDocumentationUserFlowByUserId : `documentation-approval-service/documentation-user/get-by-user-id/`,
    getDocumentationVehicleFlowByUserId : `documentation-approval-service/documentation-vehicle/get-by-user-id/`,
    getDocumentationVehicleByVehicleId : `documentation-approval-service/documentation-vehicle/vehicle/`,
    getDocumentationApprovalFlowByVehicleOptions : `documentation-approval-service/get-by-vehicle-options`,
    createDocumentationApprovalUserLevel : `documentation-approval-service/documentation-user/create-by-list`,
    createDocumentationApprovalVehicleLevel : `documentation-approval-service/documentation-vehicle/create-by-list`,
}

export const ATTENDANCE_SERVICE = {
    // color
    getAttendanceColor: `attendance-service/v1/color/get`,
    updateAttendanceColor: `attendance-service/v1/color/update`,
    resetToDefaultAttendanceColor: `attendance-service/v1/color/reset`,
    // cycle
    getAttendanceCycleConfig: `attendance-service/v1/cycle/get`,
    updateAttendanceCycleConfig: `attendance-service/v1/cycle/update`,
    // department
    getAttendanceDepartments: `attendance-service/v1/department/get-all`,
    // geoFence
    getGeofenceById : `attendance-service/v1/geofences/`,
    createGeofence : `attendance-service/v1/geofences/create`,
    updateGeofenceStatus : `attendance-service/v1/geofences/update-status`,
    updateGeofence : `attendance-service/v1/geofences/update`,
    updateGeofenceHierarchyLevel : `attendance-service/v1/geofences/update-hierarchy-Level`,
    getGeofenceList : `attendance-service/v1/geofences/get-all`,
    getGeofenceConfigSetting : `attendance-service/v1/geofence-config/geofence-id/`,
    getCustomAccessByShiftId: `attendance-service/get-all-pageable-custom-access/by-shift-id/`
}

export const EMAIL_SETTINGS_SERVICE = {
    // user-email
    getAllOnlyUserData: `email-settings-service/user-email/get-all/only-user-data`,
    updateUserEmailByList: `email-settings-service/user-email/update-list`,
    // email-settings
    updateEmailSettingAttendanceManage: `email-settings-service/update`,
    getEmailSetting: `email-settings-service/get`,
};

export const FOODIE_MENU_SERVICE = {
    checkOutOrderKitchen: `foodie-menu-service/v2/orders/check-out/`,
    getActiveExtraOrders: `foodie-menu-service/v2/extra/orders/search-active`,
    getAllMealTimes: `foodie-menu-service/v1/meal-times/get-all`,
    getCustomAccessByShiftIdFoodie : `foodie-menu-service/v1/get-all-pageable-custom-access/by-shift-id/`
};

export const ZONE_SERVICE = {
    getZoneTree: `zone-service/get-tree/father-zones`,
    getAllZoneTree: `zone-service/get-tree`
};

export const HID_SERVICE = {
    getAcrOutputsAvailableBySio: `hid-service/v1/acrs/outputs-by-sio/`,
    getAcrInputsNormallyCloseBySio: `hid-service/v1/acrs/inputs-by-sio/normally-close/`,
    getAcrInputsNormallyOpenBySio: `hid-service/v1/acrs/inputs-by-sio/normally-open/`,
    getInputTypeConnections: `hid-service/v1/input/type-connections`
}


export const CONTRACTOR_EMPLOYEE_SERVICE = {
    updateContractorEmployee: `contractor-employee-service/v1/update`,
    createContractorEmployee: `contractor-employee-service/v1/create`,
}

export const SUPPLIER_EMPLOYEE_SERVICE = {
    updateSupplierEmployee: `supplier-employee-service/v1/update`,
    createSupplierEmployee: `supplier-employee-service/v1/create`,
}

export const CONTRACTOR_SERVICE = {
    updateContractor: `contractor-service/v1/update`
}

export const CONTRACT_SERVICE = {
    getContractByIdV2: `contract-service/v2/get-by-id/`
}

export const SUPPLIER_SERVICE = {
    updateSupplier: `supplier-service/v1/update`
}

export const USER_SERVICE = {
    getUserPreferencesByUserId: `user-service/preferences/get/by-user-id/`,
    updateUserPreferences: `user-service/preferences/update`,
    createUserPreferences: `user-service/preferences/create`
}
export const ASSETS_SERVICE = {
    hardDeleteRecordsByTableAndIds: "assets-service/hard-delete-by-ids/by-table/"
}

export const VERIDAS_SERVICE = {
    getServiceById: "veridas-service/v1/get/"
}

export const APPROVAL_TRACKER_SERVICE = {
    approveMultipleRequestMeals: "approval-tracker-service/v1/approve"
}

export const FOODIE_MEAL_ENTRY_SERVICE = {
    publishDailyKitchenAccess: "/app/daily-kitchen-access",
    topicDailyKitchenAccess: "/topic/daily_kitchen_access"
}

export const FILE_SERVICE = {
    pilgrimsReports: (exportFormat) =>`file-service/${exportFormat}/pilgrims-service`
}

export const ENDECRYPT_SERVICE = {
    decrypt: `endecrypt-service/decrypt/`,
};

export const MATERIAL_REQUEST_SERVICE = {
    getMaterialRequestById: `material-request-service/v1/`,
    createMaterialRequest: `material-request-service/v1/create`,
    approvalMaterialRequest: `material-request-service/v1/approval-action`,
    dispatchMaterialRequest: `material-request-service/v1/dispatch-action`,
    createMaterialRequestItemEvidence: `material-request-service/v1/create/item-evidence`,
    updateMaterialRequestItemEvidence: `material-request-service/v1/update/item-evidence`,
    getMaterialRequestQrCode: `material-request-service/v1/qr-code/`,
}
